/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useEffect, useState, useMemo, useContext } from 'react'
import {
  Spin,
  Select,
  DatePicker,
  Button,
  Modal,
  Input,
  Switch,
  message,
  Icon,
} from 'antd'
// import { LockOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons'
import moment from 'moment'
import R from 'ramda'

import { usePmgrApi, useErrorModal } from './hooks'
import * as api from '../api/report-dashboard'
import { CriteriaRow, Filler } from 'core/screen-util'
import { UnitSelectionContext } from 'context'
import { DATE_TIME_FORMAT_API, DATE_FORMAT_UI } from '../enum'
import { getUnits, UnitSelection } from 'core'
import { XlsxExport } from 'core/component'
import { getAggregationData, visibleColumns, toXlsxColumns } from './utils'
// import * as ls from '../localStorage'
import { useWorker } from 'workers'
import { useSelector } from 'react-redux'

const { Option } = Select
const { RangePicker, MonthPicker } = DatePicker

// const LS_KEY_VIEW_NAME = 'rpt_dash_view_name'

// export const LS_KEY_GLOBAL_SELECTED_YEAR_MOMENT = 'global_selected_year_moment'

const DashboardCriteria = ({
  onRetrieve,
  tableData,
  effectiveData,
  profileData,
  setProfileData,
  availableProfiles,
  onViewSelectionChange,
  setAvailableProfiles,
}) => {
  const { selectedUnits, setSelectedUnits } = useContext(UnitSelectionContext)
  const units = useSelector(getUnits)

  const [unitSelectionVisible, setUnitSelectionVisible] = useState(true)

  const [getViews, loadingViews] = usePmgrApi(api.getViews)
  const [onError, errorModal] = useErrorModal()

  const [viewList, setViewList] = useState([])
  const [selectedViewName, setSelectedViewName] = useState(null)

  // const [caseYearMoment, setCaseYearMoment] = useState(
  //   moment()
  //   // ls.getMoment(LS_KEY_GLOBAL_SELECTED_YEAR_MOMENT)
  // )

  // useEffect(() => {
  //   ls.setMoment(LS_KEY_GLOBAL_SELECTED_YEAR_MOMENT, caseYearMoment)
  // }, [caseYearMoment])

  const [startDate, setStartDate] = useState(moment().startOf('month'))
  const [endDate, setEndDate] = useState(moment().endOf('month'))

  const selectedView = useMemo(
    () => viewList.find(item => item.viewName === selectedViewName),
    [selectedViewName, viewList]
  )

  // const [getDispatchRun, loadingDispatchRun] = usePmgrApi(api.getDispatchRun)
  // const [dispatchRunList, setDispatchRunList] = useState([])
  // const [selectedDispatchRun, setSelectedDispatchRun] = useState()

  const [selectedTransIds, setSelectedTransIds] = useState([])

  const selectedMonth = useMemo(() => startDate.month(), [startDate])
  const selectedYear = useMemo(() => startDate.year(), [startDate])

  const [showSaveProfile, setShowSaveProfile] = useState(false)
  const [isProfilePublic, setIsProfilePublic] = useState(true)

  const [newSelectionName, setNewSelectionName] = useState('')

  const [selectedProfileId, setSelectedProfileId] = useState('default')

  const [saveProfileApi, saving] = usePmgrApi(api.saveProfile)
  const [removeProfileApi] = usePmgrApi(api.deleteProfile)

  const removeProfile = () => {
    if (selectedProfileId === 'default') {
      message.error('can not delete Default profile')
    } else {
      const profileObj = {
        selectionName: selectedProfileId,
        viewName: selectedViewName,
        // passing just because it is mandatory
        isPublic: true,
      }

      Modal.confirm({
        title: `Do you want to remove '${selectedProfileId}'?`,
        onOk: () => {
          removeProfileApi(profileObj).fork(onError, () => {
            setSelectedProfileId('default')
            setProfileData({})
            setAvailableProfiles(p =>
              p.filter(i => i.selectionName !== selectedProfileId)
            )
          })
        },
        okText: 'Yes',
        cancelText: 'No',
      })
    }
  }

  const saveProfile = () => {
    if (newSelectionName.trim() === '') {
      message.info('please enter valid profile name.')
    } else {
      const isalreadyPresent = availableProfiles
        .map(ap => ap.selectionName)
        .includes(newSelectionName)
      if (isalreadyPresent) {
        message.warn('Profile Name already exits.')
        return
      }
      const profileObj = {
        selectionName: newSelectionName,
        viewName: selectedViewName,
        isPublic: isProfilePublic,
        month: selectedMonth + 1,
        year: selectedYear,
        tableWiseData: R.mapObjIndexed(
          (value, key) => ({
            ...value,
            tableName: key,
            selectionName: newSelectionName,
            viewName: selectedViewName,
          }),
          profileData
        ),
      }

      saveProfileApi(profileObj).fork(onError, () => {
        setAvailableProfiles(p => p.concat(profileObj))
        setIsProfilePublic(true)
        setNewSelectionName('')
        setShowSaveProfile(false)
        setSelectedProfileId(newSelectionName)
      })
    }
  }

  useEffect(() => {
    setSelectedUnits(units)
  }, [setSelectedUnits, units])

  useEffect(() => {
    // selectedView && ls.setString(LS_KEY_VIEW_NAME, selectedView.viewName)
    if (!(selectedView && selectedView.transIdCriteria)) {
      setSelectedTransIds([])
    }
  }, [selectedView])

  useEffect(() => {
    getViews().fork(onError, data => {
      setViewList(data)
      // const storedView = ls.getString(LS_KEY_VIEW_NAME)
      // setSelectedViewName(storedView || (data[0] && data[0].viewName))
      setSelectedViewName(data[0] && data[0].viewName)
    })
  }, [getViews, onError])

  const retrieveData = () => {
    const criteria = {
      viewName: selectedViewName,
      units: selectedUnits.map(u => u.unitId),
      startDateTime: startDate.format(DATE_TIME_FORMAT_API),
      endDateTime: endDate.format(DATE_TIME_FORMAT_API),
      // dispatchRun: selectedDispatchRun !== 'none' ? selectedDispatchRun : null,
      transactionIds: selectedTransIds,
      snapshot: false,
      // otherCriteria: null
    }

    onRetrieve(criteria)
    setUnitSelectionVisible(false)
  }

  const [utilWorker] = useWorker({
    getWorkerInstance: () =>
      new Worker('workers/rpt-dash-worker.js', { type: 'module' }),
  })

  return (
    <Spin spinning={loadingViews}>
      {errorModal}
      {showSaveProfile && (
        <Modal
          title="Create Profile"
          width="20vw"
          visible
          onOk={saveProfile}
          okText="Ok"
          onCancel={() => setShowSaveProfile(false)}
          centered
          maskClosable={false}
          okButtonProps={{
            loading: saving,
            disabled: availableProfiles
              .map(ap => ap.selectionName)
              .includes(newSelectionName),
          }}
        >
          <div css={{ marginBottom: 10 }}>
            <b> Profile Name : </b>
            <Input
              value={newSelectionName}
              onChange={e => setNewSelectionName(e.target.value)}
              maxLength={200}
            />
          </div>
          <div>
            <b> Is Public : </b>
            <Switch
              checkedChildren="public"
              unCheckedChildren="private"
              checked={isProfilePublic}
              onChange={checked => setIsProfilePublic(checked)}
              css={{ width: 80 }}
            />
          </div>
          <div style={{ marginTop: '10px', color: 'red' }}>
            {availableProfiles
              .map(ap => ap.selectionName)
              .includes(newSelectionName) && 'Profile Name already exits.'}
          </div>
        </Modal>
      )}
      <CriteriaRow css={{ padding: 5 }}>
        <table cellPadding={3} css={{ textAlign: 'right' }}>
          <colgroup>
            {/* <col css={{ width: 50 }} /> */}
            {/* <col css={{ width: 200 }} /> */}
          </colgroup>
          <tbody>
            <tr>
              <td css={{ fontWeight: 'bold' }}>View :</td>
              <td>
                <Select
                  showSearch
                  value={selectedViewName}
                  onChange={item => {
                    setSelectedViewName(item)
                    onViewSelectionChange()
                    setSelectedProfileId('default')
                    setUnitSelectionVisible(true)
                  }}
                  css={{ width: '100%', minWidth: 200, textAlign: 'left' }}
                >
                  {viewList.map(view => {
                    const viewName = view.viewName
                    return (
                      <Option key={viewName} value={viewName} title={viewName}>
                        {viewName}
                      </Option>
                    )
                  })}
                </Select>
              </td>

              {selectedView && selectedView.withDate ? (
                <>
                  <td css={{ fontWeight: 'bold' }}>Date :</td>
                  <td>
                    <RangePicker
                      allowClear={false}
                      value={[startDate, endDate]}
                      onChange={([start, end]) => {
                        setStartDate(start.startOf('hour'))
                        setEndDate(end.startOf('hour'))
                      }}
                      // showTime={{ format: 'HH' }}
                      format={DATE_FORMAT_UI}
                      css={{ width: '100%', maxWidth: 275, textAlign: 'left' }}
                    />
                  </td>
                </>
              ) : (
                <>
                  <td css={{ fontWeight: 'bold' }}>Month :</td>
                  <td>
                    <MonthPicker
                      allowClear={false}
                      css={{ width: '100%', maxWidth: 275 }}
                      value={startDate}
                      onChange={date => {
                        setStartDate(date)
                        setEndDate(date)
                      }}
                      format="MMM-YYYY"
                      // showTime={{ format: 'HH:mm' }}
                      // format="YYYY-MM-DD HH:mm"
                    />
                  </td>
                </>
              )}
              {selectedView && selectedView.transIdCriteria ? (
                <>
                  <td css={{ fontWeight: 'bold' }}>Trans IDs :</td>
                  <td>
                    <Select
                      mode="tags"
                      value={selectedTransIds}
                      onChange={item => setSelectedTransIds(item)}
                      css={{ width: '100%', maxWidth: '350px' }}
                    />
                  </td>
                </>
              ) : null}
              <td>
                <Button onClick={() => setUnitSelectionVisible(v => !v)}>
                  Units
                </Button>
              </td>
              <td>
                <Button type="primary" onClick={retrieveData}>
                  Retrieve
                </Button>
              </td>

              {effectiveData.length ? (
                <td>
                  <XlsxExport
                    text="Export"
                    fileName={selectedViewName}
                    getWorksheets={async () => {
                      const result = Promise.all(
                        tableData.map(async (table, index) => {
                          const profile = profileData[table.tableName] || {}
                          const processed = effectiveData[index] || []

                          const aggregationData = profile.calculations
                            ? getAggregationData(
                                processed,
                                table.columnHeaders,
                                profile.calculations.split(',')
                              )
                            : []

                          const visibleCols = visibleColumns(
                            profile,
                            table.columnHeaders
                          )
                          return {
                            name: table.displayName,
                            columns: toXlsxColumns(visibleCols),
                            data: await utilWorker.toXlsxData(
                              processed.concat(aggregationData),
                              visibleCols
                            ),
                            colorInfo: table.colorInfo,
                            metaInfo: table.metaInfo,
                          }
                        })
                      )
                      return result
                    }}
                  />
                </td>
              ) : null}
            </tr>
          </tbody>
        </table>
        <Filler />
        {availableProfiles && (
          <div css={{ marginRight: 10 }}>
            <b>Profile : </b>
            <Select
              value={selectedProfileId}
              css={{ width: 150 }}
              onChange={val => {
                if (val === 'default') setProfileData({})
                else {
                  const sp =
                    availableProfiles.find(p => p.selectionName === val)
                      .tableWiseData || {}
                  setProfileData(sp)
                }
                setSelectedProfileId(val)
              }}
            >
              <Option value="default">Default</Option>
              {availableProfiles.map(profile => {
                const profileName = profile.selectionName
                return (
                  <Option
                    key={profileName}
                    value={profileName}
                    title={profileName}
                  >
                    {profileName}
                    {!profile.isPublic && <Icon type="lock" />}
                  </Option>
                )
              })}
            </Select>

            <Button
              title="Remove Profile"
              css={{ marginLeft: 10 }}
              onClick={removeProfile}
              icon="delete" // {<Icon type="delete" />}
              size="small"
            />
            <Button
              title="Save Profile"
              css={{ marginLeft: 10 }}
              type="primary"
              onClick={() => setShowSaveProfile(true)}
              icon="save" // {<Icon type="save" />}
              size="small"
            />
          </div>
        )}
      </CriteriaRow>
      <div css={{ height: 5 }} />
      {/* <UnitSelectionControls /> */}
      {unitSelectionVisible && (
        <div
          css={{
            paddingLeft: '16px',
            paddingRight: '16px',
            backgroundColor: 'white',
            marginBottom: '10px',
          }}
        >
          <UnitSelection
            units={units}
            selectedUnits={selectedUnits}
            setSelectedUnits={setSelectedUnits}
          />
        </div>
      )}
    </Spin>
  )
}

export default DashboardCriteria
